import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, PackageSearch, Truck, CircleCheck, Clock3, CircleX } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_META = {
  awaiting_payment: { label: "Awaiting Payment", icon: Clock3, cls: "text-amber-600 border-amber-500/40 bg-amber-50" },
  paid: { label: "Paid — Processing", icon: CircleCheck, cls: "text-[#6D28D9] border-[#6D28D9]/40 bg-[#F3F0FF]" },
  shipped: { label: "Shipped", icon: Truck, cls: "text-emerald-600 border-emerald-500/40 bg-emerald-50" },
  cancelled: { label: "Cancelled", icon: CircleX, cls: "text-red-500 border-red-400/40 bg-red-50" },
};

const inputCls =
  "w-full bg-white border border-[#E4E4E7] px-4 py-3 text-sm text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#6D28D9] transition-colors";

export const TrackOrderModal = ({ open, onClose }) => {
  const [orderNo, setOrderNo] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const r = await api.post("/orders/track", { order_no: orderNo, email });
      setResult(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const meta = result ? STATUS_META[result.status] || STATUS_META.awaiting_payment : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
          />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              data-testid="track-order-modal"
              className="pointer-events-auto w-full max-w-md max-h-[85vh] overflow-y-auto bg-white border border-[#E4E4E7] p-8"
            >
              <button onClick={onClose} data-testid="track-order-close" className="absolute top-4 right-4 p-2 text-[#71717A] hover:text-[#09090B] transition-colors">
                <X className="w-5 h-5" />
              </button>

              <p className="font-mono-clinical text-[10px] tracking-[0.3em] uppercase text-[#6D28D9] mb-3 flex items-center gap-2">
                <PackageSearch className="w-3.5 h-3.5" strokeWidth={1.5} /> Order Status
              </p>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-[#09090B] mb-6">Track your order</h3>

              <form onSubmit={submit} className="space-y-4">
                <input
                  required
                  placeholder="Order number (e.g. C23A3C70)"
                  data-testid="track-order-number"
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  className={inputCls}
                />
                <input
                  required
                  type="email"
                  placeholder="Email used at checkout"
                  data-testid="track-order-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                />
                {error && <p data-testid="track-order-error" className="text-sm text-red-500 font-mono-clinical">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="track-order-submit"
                  className="w-full py-4 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Check Status
                </button>
              </form>

              {result && meta && (
                <div data-testid="track-order-result" className="mt-6 border-t border-[#E4E4E7] pt-5 space-y-4">
                  <div className={`inline-flex items-center gap-2 border px-3 py-1.5 font-mono-clinical text-[11px] tracking-[0.15em] uppercase ${meta.cls}`}>
                    <meta.icon className="w-4 h-4" strokeWidth={1.5} />
                    <span data-testid="track-order-status">{meta.label}</span>
                  </div>
                  <div className="text-xs text-[#52525B] space-y-1.5">
                    <p><span className="text-[#A1A1AA] uppercase tracking-wider font-mono-clinical text-[10px]">Order</span> № {result.order_no} — {result.payment_method}</p>
                    {result.items.map((i, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{i.name} {i.size} × {i.qty}</span>
                        <span className="font-mono-clinical">${(i.price * i.qty).toFixed(2)}</span>
                      </div>
                    ))}
                    {result.discount_usd > 0 && (
                      <div className="flex justify-between text-[#6D28D9]">
                        <span>New account discount</span>
                        <span className="font-mono-clinical">−${result.discount_usd.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-[#09090B] pt-2 border-t border-[#E4E4E7]">
                      <span>Total</span>
                      <span className="font-mono-clinical">${result.total_usd.toFixed(2)}</span>
                    </div>
                  </div>
                  {result.status === "awaiting_payment" && (
                    <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                      We&rsquo;re still waiting for your payment to confirm. Once detected, your order moves to processing automatically.
                    </p>
                  )}
                  {result.status === "shipped" && (
                    <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                      Your order is on its way, cold-chain packaged. Keep an eye on your inbox for tracking details.
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
